import React from 'react';
import { defaultUrlTransform } from 'react-markdown';

// "Try Yourself" sections in curriculum/self-guided content link into the
// Question Bank with a specific type/difficulty filter pre-applied, using a
// custom URI scheme markdown authors can write directly in their content:
//
//   [Practice hard Venn Diagram questions](practice:DM?type=venn_diagrams&difficulty=hard)
//   [Practice all Rates questions](practice:QR?type=rates)
//   [Practice this topic](practice:VR)
//
// A real href would just 404 (or leave the SPA) on click, so ReactMarkdown's
// `components` prop intercepts anchor clicks whose href starts with
// "practice:", parses it, and calls onStartPractice(section, filters)
// exactly like the Question Bank's own filter UI does — this is a routing
// shortcut, not a second source of truth for filter shape.
const PRACTICE_SCHEME = 'practice:';

export function isPracticeLink(href) {
  return typeof href === 'string' && href.startsWith(PRACTICE_SCHEME);
}

export function parsePracticeLink(href) {
  const rest = href.slice(PRACTICE_SCHEME.length); // "DM?type=venn_diagrams&difficulty=hard"
  const [section, queryString] = rest.split('?');
  const query = new URLSearchParams(queryString || '');
  const filters = {};
  if (query.get('type')) filters.question_type = query.get('type');
  if (query.get('difficulty')) filters.difficulty = query.get('difficulty');
  if (query.get('family')) filters.family = query.get('family');
  return { section: section.toUpperCase(), filters };
}

// react-markdown sanitizes hrefs by default (defaultUrlTransform), stripping
// any URI scheme it doesn't recognize as safe — which silently empties out
// our custom "practice:" links. Pass this as ReactMarkdown's `urlTransform`
// prop to allow that one scheme through unchanged while keeping the default
// sanitization for every other link.
export function practiceUrlTransform(url) {
  return isPracticeLink(url) ? url : defaultUrlTransform(url);
}

// Pass as ReactMarkdown's `components={{ a: ... }}` prop.
export function markdownLinkComponents(onStartPractice) {
  return {
    a: ({ href, children, ...props }) => {
      if (isPracticeLink(href)) {
        return React.createElement(
          'a',
          {
            href,
            ...props,
            onClick: (e) => {
              e.preventDefault();
              const { section, filters } = parsePracticeLink(href);
              onStartPractice(section, filters);
            },
          },
          children
        );
      }
      return React.createElement(
        'a',
        { href, target: '_blank', rel: 'noopener noreferrer', ...props },
        children
      );
    },
  };
}
